import { useMemo, useState } from "react";
import { Search, LocateFixed, Loader2 } from "lucide-react";
import { surfbreaks, type Surfbreak } from "@/data/surfbreaks";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { haversineKm } from "@/lib/surf";

interface Props {
  selectedId: string;
  onSelect: (b: Surfbreak) => void;
  userCoords?: { lat: number; lon: number } | null;
  onRequestLocation?: () => void;
  locating?: boolean;
  locationError?: string | null;
  unit?: "km" | "mi";
  onUnitChange?: (u: "km" | "mi") => void;
}

export const BreakPicker = ({ selectedId, onSelect, userCoords, onRequestLocation, locating, locationError, unit = "km", onUnitChange }: Props) => {
  const [q, setQ] = useState("");
  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    const filtered = !s
      ? surfbreaks
      : surfbreaks.filter(b =>
          [b.name, b.country, b.region].some(v => v.toLowerCase().includes(s))
        );
    if (userCoords) {
      return [...filtered].sort(
        (a, b) => haversineKm(userCoords, a) - haversineKm(userCoords, b)
      );
    }
    return filtered;
  }, [q, userCoords]);

  // Picker always renders on dark navy — use explicit light colours throughout
  const chalk = "#f0e9db";
  const fog   = "#8aacb8";
  const navy  = "hsl(205,40%,22%)";

  return (
    <div className="px-4 py-3">
      <button
        onClick={onRequestLocation}
        disabled={locating}
        style={{ color: userCoords ? "#e8500a" : chalk, borderColor: navy }}
        className="w-full mb-3 inline-flex items-center justify-center gap-2 px-4 py-2 text-xs font-bold uppercase tracking-wider border transition-all"
      >
        {locating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <LocateFixed className="h-3.5 w-3.5" />}
        {userCoords ? "Sorted by distance from you" : locating ? "Locating…" : "Use my location"}
      </button>

      {onUnitChange && (
        <div className="mb-3 inline-flex w-full p-0.5 text-xs font-bold" style={{ border: `1px solid ${navy}` }}>
          <button
            onClick={() => onUnitChange("km")}
            className={cn("flex-1 px-3 py-1.5 transition-all", unit === "km" ? "bg-gradient-sunset text-accent-foreground" : "")}
            style={unit !== "km" ? { color: fog } : {}}
          >
            Kilometres
          </button>
          <button
            onClick={() => onUnitChange("mi")}
            className={cn("flex-1 px-3 py-1.5 transition-all", unit === "mi" ? "bg-gradient-sunset text-accent-foreground" : "")}
            style={unit !== "mi" ? { color: fog } : {}}
          >
            Miles
          </button>
        </div>
      )}

      {locationError && (
        <p className="text-[11px] text-red-400 mb-2 text-center">{locationError}</p>
      )}

      <div className="relative mb-3">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: fog }} />
        <Input
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder="Search a surfbreak…"
          style={{ background: "hsl(205,55%,18%)", borderColor: navy, color: chalk }}
          className="pl-9 placeholder:text-[hsl(200,20%,45%)]"
        />
      </div>

      <ul className="max-h-[60vh] overflow-y-auto">
        {list.map(b => {
          const active = b.id === selectedId;
          const km = userCoords ? haversineKm(userCoords, b) : null;
          const mi = km !== null ? km * 0.621371 : null;
          return (
            <li key={b.id} style={{ borderBottom: `1px solid ${navy}` }} className="last:border-b-0">
              <button
                onClick={() => onSelect(b)}
                className={cn("w-full text-left px-2 py-2.5 transition-all", active ? "bg-gradient-sunset" : "")}
              >
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <div
                      className="font-semibold text-sm leading-tight"
                      style={{ color: active ? "#f0e9db" : chalk }}
                    >
                      {b.name}
                    </div>
                    <div className="text-[11px] mt-0.5" style={{ color: active ? "rgba(240,233,219,0.75)" : fog }}>
                      {b.region}, {b.country}
                    </div>
                    {km !== null && mi !== null && (
                      <div className="text-[10px] mt-0.5 font-medium" style={{ color: active ? "rgba(240,233,219,0.9)" : "#e8500a" }}>
                        {unit === "km"
                          ? `${km < 10 ? km.toFixed(1) : Math.round(km).toLocaleString()} km`
                          : `${mi < 10 ? mi.toFixed(1) : Math.round(mi).toLocaleString()} mi`}
                      </div>
                    )}
                  </div>
                  <span
                    className="text-[9px] uppercase tracking-widest font-bold shrink-0"
                    style={{ color: active ? "rgba(240,233,219,0.8)" : fog }}
                  >
                    {b.difficulty}
                  </span>
                </div>
              </button>
            </li>
          );
        })}
        {list.length === 0 && (
          <li className="text-sm text-center py-6" style={{ color: fog }}>No breaks match "{q}"</li>
        )}
      </ul>
    </div>
  );
};
